import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import useStyles from "./CheckBoxRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import { ChoiceHandler } from "../../choices/BaseChoice";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { CheckBoxIcon } from "../../shared/CheckBoxIcon";
import { translate } from "../../../models/utilities/translationUtilities";
import { removeHtmlTags } from "../../../utils";

interface Props extends RendererProps {
    choice: ChoiceModel;
    id?: string;
    name?: string;
    value?: string;
    onSelect?(): void;
    checked?: boolean;
    isDisabled?: boolean;
}

function CheckBoxRendererImpl(props: Props, ref: React.MutableRefObject<ChoiceHandler>) {
    const { choice, checked, gridRendering, onSelect, isDisabled, language } = props;

    const [hover, setHover] = React.useState(false);
    const theme = useTheme();
    const classes = useStyles({ theme });

    React.useImperativeHandle(ref, () => ({
        select() {
            onSelect();
        },
    }));

    if (!gridRendering) {
        return (
            <CheckBoxIcon checked={checked} disabled={isDisabled} backgroundless={gridRendering} />
        );
    }

    const choiceText = removeHtmlTags(translate(choice, "TEXT", language));

    return (
        <div
            id={`${choice.elementId}_INPUT`}
            role="checkbox"
            aria-checked={checked}
            aria-label={choiceText}
            tabIndex={0}
            onKeyPress={(event) => {
                if (event.key === " ") {
                    onSelect();
                    event.preventDefault();
                }
            }}
            onFocus={() => setHover(true)}
            onBlur={() => setHover(false)}
            onClick={() => onSelect()}
            onMouseOver={() => setHover(true)}
            onMouseOut={() => setHover(false)}
            className={classnames(
                classes.checkBox,
                gridRendering && classes.checkBoxGridCell,
                gridRendering && checked && classes.checkBoxGridCellChecked,
            )}
        >
            <CheckBoxIcon
                checked={checked}
                disabled={isDisabled}
                hover={hover}
                backgroundless={gridRendering}
            />
        </div>
    );
}

const CheckBoxRenderer = React.forwardRef(CheckBoxRendererImpl);

export default CheckBoxRenderer;
